import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getJobs, updateJobInArray } from '../../redux/features/jobSlice';
import { Calendar, Clock, MapPin, User, CheckCircle, AlertCircle, RefreshCw, X, LogOut } from 'lucide-react';
import CalendarWithDragDrop from '../dashboard/CalendarWithDragDrop';
import { shouldApplyJobSocketPayload, jobBelongsToTenant } from '../../utils/tenantUtils';
import JobQueue from '../dashboard/JobQueue';
import JobStatusControls from '../worker/JobStatusControls';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { normalizeWorkerId, isJobAssignedToWorker } from '../../utils/workerUtils';
import ErrorBoundary from '../ErrorBoundary';
import { DragDropContext } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../redux/features/authSlice';
import WorkerProfileView from './profileview/WorkerProfileView';
import { formatDisplayValue, getJobDisplayStatus } from '../../utils/displayUtils';

const WorkerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { jobs, loading } = useSelector((state) => state.job);
  const { user } = useSelector((state) => state.auth);
  const [showProfile, setShowProfile] = useState(false);
  
  // Safely get WebSocket - handle case where it might not be available
  // Note: React hooks must be called unconditionally, so we can't use try-catch
  // If WebSocket fails, the hook will throw and component will crash
  // We need to ensure WebSocketContext is always available
  const wsContext = useWebSocket();
  const socket = wsContext?.socket || null;
  const isConnected = wsContext?.isConnected || false;
  
  // Log WebSocket status for debugging mobile issues
  useEffect(() => {
    if (typeof window !== 'undefined') {
      console.log('📱 WorkerDashboard: WebSocket status', { 
        hasSocket: !!socket, 
        isConnected,
        hasContext: !!wsContext,
        userAgent: navigator?.userAgent || 'N/A',
        isMobile: window.innerWidth < 768
      });
    }
  }, [socket, isConnected, wsContext]);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  const [viewMode, setViewMode] = useState(() => {
    // Default to 'queue' on mobile for better UX
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 'queue';
    }
    return 'calendar';
  });
  const [calendarViewMode, setCalendarViewMode] = useState('day'); // 'day', 'week', 'month'
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const selectedJobRef = useRef(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const hasSeenLoadingRef = useRef(false);
  
  // Global settings for AI and Parking - saved in localStorage
  const [globalUseAI, setGlobalUseAI] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('worker_global_useAI') === 'true';
  });
  const [globalEnableParking, setGlobalEnableParking] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('worker_global_enableParking') === 'true';
  });
  
  // Save global settings to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('worker_global_useAI', globalUseAI.toString());
    }
  }, [globalUseAI]);
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('worker_global_enableParking', globalEnableParking.toString());
    }
  }, [globalEnableParking]);

  // Callback to receive view mode from calendar
  const handleCalendarViewModeChange = (newViewMode) => {
    setCalendarViewMode(newViewMode);
  };

  // Callback to handle job selection
  const handleJobSelect = (job) => {
    console.log('Job selected:', job);
    selectedJobRef.current = job;
    setSelectedJob(job);
  };

  useEffect(() => {
    selectedJobRef.current = selectedJob;
  }, [selectedJob]);

  // Callback to handle job status update
  const handleJobStatusUpdate = (newStatus) => {
    console.log('Job status updated:', newStatus);
    // Zatvori modal nakon arhiviranja posla
    if (newStatus === 'Completed') {
      setSelectedJob(null);
    }
    // WebSocket will handle real-time updates automatically
    // No need to refresh jobs manually
  };

  // Manual refresh function
  const handleManualRefresh = () => {
    const businessType = user?.result?.businessType || 'Home Appliance Technician';
    console.log('🔄 Manual refresh triggered by user');
    dispatch(getJobs(businessType));
  };

  useEffect(() => {
    if (loading) {
      hasSeenLoadingRef.current = true;
      return;
    }

    if (hasSeenLoadingRef.current && !hasLoadedOnce) {
      setHasLoadedOnce(true);
    }
  }, [loading, hasLoadedOnce]);

  useEffect(() => {
    if (!hasLoadedOnce && Array.isArray(jobs) && jobs.length > 0) {
      setHasLoadedOnce(true);
    }
  }, [jobs, hasLoadedOnce]);

  // Handle mobile detection and auto-switch to queue view
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Auto-switch to queue view on mobile for better UX
      if (mobile && viewMode === 'calendar') {
        setViewMode('queue');
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  useEffect(() => {
    // Get user from Redux or localStorage as fallback
    // This is critical for mobile devices where Redux might not be initialized yet
    const currentUser = user?.result || (() => {
      try {
        if (typeof window === 'undefined' || !window.localStorage) {
          console.warn('⚠️ localStorage not available');
          return null;
        }
        const profileStr = localStorage.getItem('profile');
        if (!profileStr) {
          console.warn('⚠️ No profile in localStorage');
          return null;
        }
        const profile = JSON.parse(profileStr);
        const result = profile?.result;
        console.log('📱 WorkerDashboard: Got user from localStorage', {
          hasResult: !!result,
          hasFirstName: !!result?.firstName,
          hasBusinessType: !!result?.businessType
        });
        return result;
      } catch (e) {
        console.error('❌ Error reading from localStorage:', e);
        return null;
      }
    })();
    
    if (!currentUser) {
      console.error('❌ WorkerDashboard: No user found in Redux or localStorage!', {
        hasReduxUser: !!user?.result,
        hasLocalStorage: typeof window !== 'undefined' && !!window.localStorage,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
        isMobile: typeof window !== 'undefined' ? window.innerWidth < 768 : false
      });
      // Don't return early - try to show error message instead of blank screen
      // This is especially important for mobile devices
      return;
    }
    
    // Get business type from user or use default
    const businessType = currentUser?.businessType || 'Home Appliance Technician';
    console.log('📱 WorkerDashboard: Fetching jobs', {
      hasUser: !!currentUser,
      businessType,
      userFromRedux: !!user?.result,
      userFromStorage: !!currentUser,
      userId: currentUser?._id,
      userIdType: typeof currentUser?._id,
      userBusinessType: currentUser?.businessType,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
      isMobile: typeof window !== 'undefined' ? window.innerWidth < 768 : false
    });
    
    try {
      console.log('🔄 WorkerDashboard: Dispatching getJobs with businessType:', businessType);
      dispatch(getJobs(businessType));
    } catch (error) {
      console.error('❌ Error dispatching getJobs:', error);
    }
    
    // No more auto-refresh - WebSocket will handle real-time updates
  }, [dispatch, user]);

  // WebSocket listener for real-time job updates
  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }
    
    try {
      // Listen for job updates
      socket.on('job_updated', (data) => {
        if (!shouldApplyJobSocketPayload(data)) return;

        console.log('📋 Real-time job update received:', data);

        if (data.job && data.job._id) {
          dispatch(updateJobInArray({ jobId: data.job._id, updates: data.job }));
        } else if (data.jobId) {
          const updates = {};
          if (data.status !== undefined) updates.status = data.status;
          if (data.travelStartTime !== undefined) updates.travelStartTime = data.travelStartTime;
          if (data.workStartTime !== undefined) updates.workStartTime = data.workStartTime;
          if (data.accumulatedTravelTime !== undefined) updates.accumulatedTravelTime = data.accumulatedTravelTime;
          if (data.accumulatedWorkTime !== undefined) updates.accumulatedWorkTime = data.accumulatedWorkTime;
          if (Object.keys(updates).length > 0) {
            dispatch(updateJobInArray({ jobId: data.jobId, updates }));
          }
        }

        const sel = selectedJobRef.current;
        if (sel && data.jobId === sel._id) {
          if (data.status === 'Archived') {
            console.log('🎯 Job completed, closing modal and refreshing jobs');
            selectedJobRef.current = null;
            setSelectedJob(null);
          } else if (data.job) {
            console.log('🔄 Updating selected job with new data:', data.job);
            selectedJobRef.current = data.job;
            setSelectedJob(data.job);
          }
        }
      });

      // Novi posao kreiran – osvežiti listu samo ako je posao dodeljen ovom radniku (real-time, bez osvežavanja stranice)
      socket.on('job_created', (data) => {
        console.log('📋 Real-time job created received:', data);
        if (!data.job || !jobBelongsToTenant(data.job)) return;

        const currentUser = user?.result || (() => {
          try {
            const profile = JSON.parse(localStorage.getItem('profile') || '{}');
            return profile?.result;
          } catch (e) {
            return null;
          }
        })();
        if (!currentUser || !data.job) return;
        const workerId = String(currentUser._id);
        const jobAssignedTo = String(data.job.assignedTo || '').trim();
        // assignedTo može biti "id" ili "worker:id" (prefix iz forme)
        const isAssignedToMe = jobAssignedTo && jobAssignedTo !== 'Unassigned' &&
          (jobAssignedTo === workerId || jobAssignedTo === `worker:${workerId}`);
        if (isAssignedToMe) {
          console.log('✅ Novi posao dodeljen meni, osvežavam listu');
          const businessType = currentUser.businessType || 'Home Appliance Technician';
          dispatch(getJobs(businessType));
        }
      });

      return () => {
        try {
          socket.off('job_updated');
          socket.off('job_created');
        } catch (error) {
          console.warn('Error removing WebSocket listener:', error);
        }
      };
    } catch (error) {
      console.error('Error setting up WebSocket listener:', error);
    }
  }, [socket, isConnected, dispatch, user]);

  useEffect(() => {
    // Get user from Redux or localStorage as fallback
    const currentUser = user?.result || (() => {
      try {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}');
        return profile?.result;
      } catch (e) {
        return null;
      }
    })();
    
    console.log('🔄 WorkerDashboard: Jobs filtering effect triggered', {
      hasJobs: !!jobs,
      jobsLength: Array.isArray(jobs) ? jobs.length : 'not an array',
      jobsType: typeof jobs,
      hasCurrentUser: !!currentUser,
      currentUserId: currentUser?._id,
      currentUserIdType: typeof currentUser?._id
    });
    
    if (jobs && currentUser) {
      // Filter jobs assigned to this worker
      const workerId = currentUser._id;
      
      console.log('🔍 WorkerDashboard: Filtering jobs', {
        workerId,
        workerIdType: typeof workerId,
        totalJobs: Array.isArray(jobs) ? jobs.length : 0,
        jobsIsArray: Array.isArray(jobs),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
        isMobile: typeof window !== 'undefined' ? window.innerWidth < 768 : false
      });
      
      // Log sample of job assignedTo values for debugging
      if (Array.isArray(jobs) && jobs.length > 0) {
        const sampleAssignedTo = jobs.slice(0, 5).map(j => ({
          jobId: j?._id,
          assignedTo: j?.assignedTo,
          assignedToType: typeof j?.assignedTo,
          assignedToValue: j?.assignedTo
        }));
        console.log('  Sample job assignedTo values:', sampleAssignedTo);
      }
      
      // Safely log jobs sample - handle potential errors
      try {
        if (Array.isArray(jobs) && jobs.length > 0) {
          console.log('  Jobs sample:', jobs.slice(0, 3).map(j => ({
            id: j?._id || 'N/A',
            assignedTo: j?.assignedTo || 'N/A',
            assignedToType: typeof j?.assignedTo,
            clientName: j?.clientName || 'N/A'
          })));
        }
      } catch (sampleError) {
        console.error('  ❌ Error logging jobs sample:', sampleError);
      }
      
      // Use the utility function for reliable ID comparison
      // Wrap in try-catch to prevent crashes when jobs have invalid data
      let assignedJobs = [];
      try {
        if (!Array.isArray(jobs)) {
          console.error('  ❌ Jobs is not an array!', typeof jobs, jobs);
          setFilteredJobs([]);
          return;
        }
        
        assignedJobs = jobs.filter(job => {
          try {
            // Skip invalid jobs
            if (!job || !job._id) {
              console.warn('  ⚠️ Skipping invalid job:', job);
              return false;
            }
            
            const isAssigned = isJobAssignedToWorker(job, workerId);
            if (isAssigned) {
              console.log('  ✅ Job assigned:', {
                jobId: job._id,
                assignedTo: job.assignedTo,
                assignedToType: typeof job.assignedTo,
                workerId: workerId,
                workerIdType: typeof workerId
              });
            } else {
              // Log why job is not assigned (only for first few jobs to avoid spam)
              if (assignedJobs.length < 3) {
                console.log('  ❌ Job NOT assigned:', {
                  jobId: job._id,
                  assignedTo: job.assignedTo,
                  assignedToType: typeof job.assignedTo,
                  workerId: workerId,
                  workerIdType: typeof workerId,
                  normalizedJobAssignedTo: normalizeWorkerId(job.assignedTo),
                  normalizedWorkerId: normalizeWorkerId(workerId)
                });
              }
            }
            return isAssigned;
          } catch (filterError) {
            console.error('  ❌ Error filtering job:', job?._id, filterError);
            return false; // Skip this job if there's an error
          }
        });
      } catch (filterError) {
        console.error('  ❌ Critical error filtering jobs:', filterError);
        console.error('  Error details:', {
          message: filterError.message,
          stack: filterError.stack,
          jobsType: typeof jobs,
          jobsLength: Array.isArray(jobs) ? jobs.length : 'N/A'
        });
        // Set empty array to prevent crash
        setFilteredJobs([]);
        return;
      }
      
      console.log('  Filtered jobs count:', assignedJobs.length);
      
      // Safely log filtered jobs
      try {
        if (assignedJobs.length > 0) {
          console.log('  Filtered jobs:', assignedJobs.slice(0, 5).map(j => ({ 
            id: j?._id || 'N/A', 
            clientName: j?.clientName || 'N/A' 
          })));
        }
      } catch (logError) {
        console.error('  ❌ Error logging filtered jobs:', logError);
      }
      
      // Safely set filtered jobs
      try {
        setFilteredJobs(assignedJobs);
      } catch (setError) {
        console.error('  ❌ Error setting filteredJobs:', setError);
        // Try to set empty array as fallback
        try {
          setFilteredJobs([]);
        } catch (fallbackError) {
          console.error('  ❌ Critical: Cannot set filteredJobs at all!', fallbackError);
        }
      }
      
      // Update selectedJob if it exists in the updated jobs list
      if (selectedJob) {
        const updatedJob = jobs.find(job => job._id === selectedJob._id);
        if (updatedJob) {
          setSelectedJob(updatedJob);
        }
      }
    } else if (jobs && jobs.length > 0 && !currentUser) {
      // If we have jobs but no user, try to get user from localStorage more aggressively
      console.log('⚠️ WorkerDashboard: Jobs available but user not found, trying localStorage...');
      try {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}');
        const token = localStorage.getItem('token');
        if (profile?.result && token) {
          console.log('✅ Found user in localStorage, retrying filter...');
          const workerId = profile.result._id;
          const assignedJobs = jobs.filter(job => isJobAssignedToWorker(job, workerId));
          console.log('  Filtered jobs count:', assignedJobs.length);
          setFilteredJobs(assignedJobs);
        }
      } catch (e) {
        console.error('Error reading from localStorage:', e);
      }
    } else {
      console.log('⚠️ WorkerDashboard: Missing data', {
        hasJobs: !!jobs,
        hasUser: !!currentUser,
        jobsLength: jobs?.length,
        userFromRedux: !!user?.result,
        userFromStorage: !!currentUser
      });
    }
  }, [jobs, user]);

  // Calculate statistics - show ALL assigned jobs, not filtered by date
  const calculateStatistics = (viewMode = 'day') => {
    // Use all filteredJobs for statistics (all assigned jobs)
    // The calendar view mode is only for display, not for statistics
    const allAssignedJobs = Array.isArray(filteredJobs) ? filteredJobs : [];
    
    try {
      return {
        totalJobs: allAssignedJobs.length,
        scheduledJobs: allAssignedJobs.filter(job => {
          try {
            return job?.serviceDate && job?.status !== 'Completed' && job?.status !== 'Cancelled';
          } catch {
            return false;
          }
        }).length,
        activeJobs: allAssignedJobs.filter(job => {
          try {
            return job?.status === 'In Repair' || job?.status === 'Diagnosing' || job?.status === 'On Road' || job?.status === 'At Client';
          } catch {
            return false;
          }
        }).length,
        completedJobs: allAssignedJobs.filter(job => {
          try {
            return job?.status === 'Finished' || job?.status === 'Completed';
          } catch {
            return false;
          }
        }).length
      };
    } catch (statsError) {
      console.error('❌ Error calculating statistics:', statsError);
      // Return safe defaults
      return {
        totalJobs: 0,
        scheduledJobs: 0,
        activeJobs: 0,
        completedJobs: 0
      };
    }
  };

  // Calculate progress percentage based on view mode
  const calculateProgress = (viewMode, currentCount) => {
    const limits = {
      day: 10,    // 10 jobs per day
      week: 50,   // 50 jobs per week  
      month: 200  // 200 jobs per month
    };
    
    const limit = limits[viewMode] || limits.day;
    return Math.min((currentCount / limit) * 100, 100);
  };

  const stats = calculateStatistics(calendarViewMode);
  const progressPercentage = calculateProgress(calendarViewMode, stats.totalJobs);

  const showInitialLoader = !hasLoadedOnce && loading;
  
  // Check if we have user data - if not, show error message instead of blank screen
  // This is critical for mobile devices where localStorage might be blocked or Redux not initialized
  const currentUserCheck = user?.result || (() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const profileStr = localStorage.getItem('profile');
        if (profileStr) {
          const profile = JSON.parse(profileStr);
          return profile?.result;
        }
      }
    } catch (e) {
      console.error('Error checking user:', e);
    }
    return null;
  })();

  // Safety check - ensure we have basic data before rendering
  if (showInitialLoader) {
    return (
      <div className="bg-gray-900 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // If no user found after loading, show error message instead of blank screen (especially for mobile)
  if (!currentUserCheck && !loading && hasLoadedOnce) {
    console.error('❌ WorkerDashboard: No user found after loading!', {
      hasReduxUser: !!user?.result,
      hasLocalStorage: typeof window !== 'undefined' && !!window.localStorage,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
      isMobile: typeof window !== 'undefined' ? window.innerWidth < 768 : false
    });
    
    return (
      <div className="bg-gray-900 min-h-screen flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white text-center mb-2">Greška pri učitavanju</h2>
          <p className="text-gray-400 text-center mb-4">
            Nije moguće učitati podatke korisnika. Molimo proverite da li ste prijavljeni i pokušajte ponovo.
          </p>
          <button
            onClick={() => {
              // Clear storage and redirect to login
              if (typeof window !== 'undefined') {
                localStorage.clear();
                sessionStorage.clear();
                // Use absolute URL for better mobile compatibility
                const baseUrl = window.location.origin;
                window.location.replace(`${baseUrl}/auth?role=worker&type=login`);
              }
            }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors mb-2"
          >
            Prijavite se ponovo
          </button>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Osveži stranicu
          </button>
        </div>
      </div>
    );
  }

  // Get company info for display
  // Try multiple sources: currentUserCheck, user from Redux, or localStorage
  const getCompanyInfo = () => {
    console.log('🔍 WorkerDashboard: Getting company info', {
      hasCurrentUserCheck: !!currentUserCheck,
      currentUserCheckCompanyName: currentUserCheck?.companyName,
      currentUserCheckCountryCode: currentUserCheck?.countryCode,
      hasReduxUser: !!user?.result,
      reduxUserCompanyName: user?.result?.companyName,
      reduxUserCountryCode: user?.result?.countryCode
    });
    
    // First try from currentUserCheck (from localStorage or Redux)
    if (currentUserCheck?.companyName) {
      console.log('✅ Using company info from currentUserCheck');
      return {
        companyName: currentUserCheck.companyName,
        countryCode: (currentUserCheck.countryCode || 'ba').toLowerCase()
      };
    }
    
    // Try from Redux user
    if (user?.result?.companyName) {
      console.log('✅ Using company info from Redux user');
      return {
        companyName: user.result.companyName,
        countryCode: (user.result.countryCode || 'ba').toLowerCase()
      };
    }
    
    // Try from localStorage directly
    try {
      const profileStr = localStorage.getItem('profile');
      if (profileStr) {
        const profile = JSON.parse(profileStr);
        if (profile?.result?.companyName) {
          console.log('✅ Using company info from localStorage');
          return {
            companyName: profile.result.companyName,
            countryCode: (profile.result.countryCode || 'ba').toLowerCase()
          };
        }
      }
    } catch (e) {
      console.error('Error reading company info from localStorage:', e);
    }
    
    // Fallback
    console.warn('⚠️ Using fallback company info');
    return {
      companyName: 'Kompanija',
      countryCode: 'ba'
    };
  };
  
  const { companyName, countryCode } = getCompanyInfo();

  // Ensure filteredJobs is always an array and safely filter
  const safeFilteredJobs = Array.isArray(filteredJobs) ? filteredJobs : [];
  
  // Safely filter jobs - handle potential errors
  const scheduledJobs = safeFilteredJobs.filter(job => {
    try {
      return job?.serviceDate && job?.scheduledTime;
    } catch {
      return false;
    }
  });
  
  const unscheduledJobs = safeFilteredJobs.filter(job => {
    try {
      return !job?.serviceDate || !job?.scheduledTime;
    } catch {
      return false;
    }
  });

  return (
    <div className="relative bg-gray-900 min-h-screen">
      {/* Company Header - Above all sections */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl font-semibold text-white">
              {companyName}
            </span>
            <img 
              src={`https://flagcdn.com/${countryCode}.svg`}
              alt={`${countryCode} flag`}
              className="h-5 w-8 rounded shadow"
              onError={(e) => {
                e.target.src = `https://flagcdn.com/ba.svg`;
              }}
            />
          </div>
        </div>
      </div>
      {loading && hasLoadedOnce && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="w-full mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 min-h-[64px]">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-2 sm:ml-4 flex-1 min-w-0">
                <h1 className="text-base sm:text-xl font-semibold text-white truncate">
                  Dobrodošli, {(() => {
                    const currentUser = user?.result || (() => {
                      try {
                        const profile = JSON.parse(localStorage.getItem('profile') || '{}');
                        return profile?.result;
                      } catch (e) {
                        return null;
                      }
                    })();
                    return currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() : 'Radnik';
                  })()}
                </h1>
                <p className="text-xs sm:text-sm text-gray-400 truncate">Vaši dodeljeni poslovi ({safeFilteredJobs.length})</p>
              </div>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* View Toggle */}
              <div className="hidden sm:flex bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'calendar'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Kalendar
                </button>
                <button
                  onClick={() => setViewMode('queue')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'queue'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <Clock className="h-4 w-4 inline mr-2" />
                  Red čekanja
                </button>
              </div>
              
              {/* Profile Button */}
              <button
                onClick={() => setShowProfile(true)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                title="Moj Profil"
              >
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">Profil</span>
              </button>
              
              {/* Logout Button */}
              <button
                onClick={() => {
                  if (window.confirm('Da li ste sigurni da želite da se izlogujete?')) {
                    // logout() function already handles redirect with absolute URL for mobile compatibility
                    dispatch(logout());
                  }
                }}
                className="flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                title="Izloguj se"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Izloguj se</span>
              </button>
              
              {/* Refresh Button */}
              <button
                onClick={handleManualRefresh}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
                title="Osveži podatke"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Osveži</span>
              </button>
            </div>
          </div>
        </div>
      </div>

        {/* Stats Cards */}
      <div className="w-full mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Ukupno poslova</p>
                <p className="text-2xl font-semibold text-white">{stats.totalJobs}</p>
                <div className="mt-2">
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{progressPercentage.toFixed(1)}% od maksimuma</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-8 w-8 text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Zakazani poslovi</p>
                <p className="text-2xl font-semibold text-white">{stats.scheduledJobs}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-8 w-8 text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Aktivni poslovi</p>
                <p className="text-2xl font-semibold text-white">{stats.activeJobs}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MapPin className="h-8 w-8 text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Završeni poslovi</p>
                <p className="text-2xl font-semibold text-white">{stats.completedJobs}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-gray-800 rounded-lg shadow-lg w-full overflow-hidden">
          {viewMode === 'calendar' ? (
            <>
              {/* Wrap CalendarWithDragDrop in error boundary since it uses react-beautiful-dnd
                  which requires Redux Provider (which should be available from index.js) */}
              <ErrorBoundary>
                <div className="w-full overflow-x-auto">
                  <CalendarWithDragDrop 
                    jobs={safeFilteredJobs}
                    viewMode="day"
                    showTimeSlots={true}
                    onViewModeChange={handleCalendarViewModeChange}
                    onJobSelect={handleJobSelect}
                    schedulerAudience="worker"
                  />
                </div>
              </ErrorBoundary>
            </>
          ) : (
            <div className="p-2 sm:p-4 w-full overflow-x-auto">
              {safeFilteredJobs.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg mb-2">Nema dodeljenih poslova</p>
                  <p className="text-gray-500 text-sm">Poslovi koji vam budu dodeljeni će se pojaviti ovde</p>
                </div>
              ) : (
                <div>
                  <div className="mb-4 px-2 sm:px-0">
                    <p className="text-sm text-gray-400">
                      Prikazano: <span className="text-white font-medium">{safeFilteredJobs.length}</span> dodeljenih poslova
                    </p>
                  </div>
                  {/* Wrap JobQueue in DragDropContext since it uses Droppable/Draggable from react-beautiful-dnd */}
                  <DragDropContext onDragEnd={() => {}}>
                    <JobQueue 
                      jobs={safeFilteredJobs}
                      loading={loading}
                      showStats={false}
                      showAllJobs={true}
                      onJobSelect={handleJobSelect}
                    />
                  </DragDropContext>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Job Details Modal with Worker Controls */}
        {selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-gray-800 rounded-lg p-4 sm:p-6 max-w-4xl w-full max-h-[95vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-white">Job Details</h3>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Left Column - Job Info */}
                <div className="space-y-4">
                  {/* Client Information */}
                  <div className="bg-gray-700 rounded p-4">
                    <h4 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Client Info
                    </h4>
                    <div className="text-sm text-gray-300 space-y-2">
                      <div className="flex gap-2">
                        <span className="text-gray-400 min-w-[100px]">Name:</span>
                        <span className="flex-1 font-medium">{selectedJob.clientName}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-gray-400 min-w-[100px]">Phone:</span>
                        <span className="flex-1">{selectedJob.clientPhone}</span>
                      </div>
                      {selectedJob.clientAddress && (
                        <div className="flex gap-2">
                          <span className="text-gray-400 min-w-[100px]">Address:</span>
                          <span className="flex-1">{selectedJob.clientAddress}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Service Details */}
                  <div className="bg-gray-700 rounded p-4">
                    <h4 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Service Details
                    </h4>
                    <div className="text-sm text-gray-300 space-y-2">
                      {selectedJob.serviceDate && (
                        <div className="flex gap-2">
                          <span className="text-gray-400 min-w-[100px]">Date:</span>
                          <span className="flex-1">{new Date(selectedJob.serviceDate).toLocaleDateString('sr-RS')}</span>
                        </div>
                      )}
                      {selectedJob.scheduledTime && (
                        <div className="flex gap-2">
                          <span className="text-gray-400 min-w-[100px]">Time:</span>
                          <span className="flex-1">{selectedJob.scheduledTime}</span>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <span className="text-gray-400 min-w-[100px]">Status:</span>
                        <span className="flex-1">{getJobDisplayStatus(selectedJob)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Issue Description */}
                  {selectedJob.issueDescription && formatDisplayValue(selectedJob.issueDescription) !== '-' && (
                    <div className="bg-gray-700 rounded p-4">
                      <h4 className="font-semibold text-white text-sm mb-3">Issue Description</h4>
                      <p className="text-sm text-gray-300">{formatDisplayValue(selectedJob.issueDescription)}</p>
                    </div>
                  )}
                </div>

                {/* Right Column - Worker Controls */}
                <div className="space-y-4">
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Worker Controls
                    </h4>
                    <JobStatusControls 
                      job={selectedJob}
                      user={user}
                      globalUseAI={globalUseAI}
                      globalEnableParking={globalEnableParking}
                      onStatusUpdate={handleJobStatusUpdate}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Profile Modal */}
      {showProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 max-w-4xl w-full max-h-[95vh] overflow-y-auto relative">
            <button
              onClick={() => setShowProfile(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
            <WorkerProfileView />
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerDashboard;
